// Charts.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
} from "recharts";

function niceStep(raw) {
  if (raw <= 0) return 1;
  const base = Math.pow(10, Math.floor(Math.log10(raw)));
  const candidates = [1, 2, 3, 5, 10].map((m) => m * base);
  return candidates.find((c) => c >= raw) ?? 10 * base;
}
function getYAxisTicks(maxValue) {
  const rawStep = Math.ceil((maxValue || 0) / 5);
  const step = niceStep(rawStep);
  return [0, ...Array.from({ length: 5 }, (_, i) => step * (i + 1))];
}

/**
 * Props:
 * - title?: string
 * - data: Array<{ day: number|string, value: number }>
 * - maxValue: number
 * - height?: number (default 320)
 * - pxPerPoint?: number (default 40)
 */
export default function Charts({
  title = "Chart",
  data = [],
  maxValue = 0,
  height = 400,
  pxPerPoint = 40,
}) {
  const wrapperRef = useRef(null);
  const [containerW, setContainerW] = useState(600);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setContainerW(el.clientWidth || 600);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { sorted, ticks, yMax, step, chartW } = useMemo(() => {
    const sorted = [...data].sort((a, b) => Number(a.day) - Number(b.day));
    const fallbackMax = Math.max(0, ...sorted.map((d) => d.value));
    const rawStep = Math.ceil((maxValue || fallbackMax) / 5);
    const step = niceStep(rawStep);
    const ticks = [0, ...Array.from({ length: 5 }, (_, i) => step * (i + 1))];
    const yMax = ticks[ticks.length - 1];
    const pointsWidth = Math.max(sorted.length, 1) * pxPerPoint;
    let chartW = pointsWidth;
    if (pointsWidth < containerW) {
      chartW = containerW;
    }
    return { sorted, ticks, yMax, step, chartW };
  }, [data, maxValue, pxPerPoint, containerW]);

  return (
    <div style={{ width: "100%", display: "grid", gap: 8 }}>
      {title && <div style={{ fontSize: 26 }}>{title}</div>}

      <div
        ref={wrapperRef}
        style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}
      >
        <div
          style={{
            width: chartW,
            borderRadius: 8,
            background: "#fff",
            padding: 8,
          }}
        >
          <LineChart
            width={chartW - 16}
            height={height}
            data={sorted}
            margin={{ top: 20, right: 16, bottom: 12, left: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
              dataKey="day"
              tickMargin={10}
              padding={{ left: 12, right: 12 }}
              axisLine
            />

            <YAxis
              domain={[-step / 2, yMax]}
              ticks={ticks}
              tickMargin={10}
              width={36}
              axisLine
            />

            <Tooltip />
            <Line
              type="linear"
              dataKey="value"
              stroke="#ff3b30"
              strokeWidth={2}
              dot={{ r: 3 }}
              isAnimationActive={false}
            >
              <LabelList dataKey="value" position="top" />
            </Line>
          </LineChart>
        </div>
      </div>
    </div>
  );
}
